/**
 * AiDashboard — AI Use Case Register overview (route /ai-use-cases/dashboard).
 *
 * Ported 1:1 from the FAIR app's AI Governance dashboard. Every count here is
 * all-time and unfiltered, exactly as in the source — no date windows, no
 * deltas, no trend lines.
 */

import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { aiUseCaseApi, peopleApi } from '../../services/api';
import { PageHeader } from '../../components/PageHeader';
import { NimbusTable } from '../../components/NimbusTable';
import {
  AI_TYPES,
  AI_TYPE_LABELS,
  AI_TYPE_ICONS,
  AI_TYPE_TONE,
  AI_STATUS_LABELS,
  AI_STATUS_TONE,
} from '../../constants/aiGovernance';
import {
  AI_ACT_CLASSIFICATIONS,
  AI_ACT_CLASSIFICATION_ICONS,
  AI_ACT_CLASSIFICATION_LABELS,
  AI_ACT_CLASSIFICATION_TONE,
  isClassified,
} from '../../constants/aiAct';
import type { AiActClassification, AiUseCase } from '../../types/models';
import { aiUseCaseKeys } from './aiUseCaseKeys';

/** How many rows the "Recent Use Cases" table shows (the source shows 6). */
const RECENT_LIMIT = 6;

/** One tile in a stat row. Extracted because there are now three of these rows and they were
 *  being hand-duplicated — the privacy pair below was four copies of the same markup. */
type Tile = {
  label: string;
  value: number;
  icon: string;
  /** Tone of the icon badge. */
  tone: string;
  /**
   * Tone of the number itself, when it should carry colour as well as the badge. Separate from
   * `tone` and opt-in because the ported status row deliberately leaves its numbers in body
   * colour — a row of five coloured numbers reads as five warnings.
   */
  valueTone?: string;
  /** Optional line under the value — the denominator, or the reason the tile exists. */
  trend?: string;
  /** Where clicking this tile should take you — usually the register, pre-filtered to just
   *  the slice this tile counts. Omit for a tile with no corresponding filtered view. */
  to?: string;
};

const StatTileRow: React.FC<{ tiles: Tile[]; grid: string }> = ({ tiles, grid }) => (
  <div className={`np-grid ${grid}`}>
    {tiles.map((tile) => {
      const body = (
        <div className="np-card-body np-stat">
          <span className={`np-stat-icon np-tone-${tile.tone}`}>
            <i className={tile.icon}></i>
          </span>
          <p className="np-stat-label">{tile.label}</p>
          <p className={`np-stat-value np-tabular${tile.valueTone ? ` np-tone-${tile.valueTone}` : ''}`}>
            {tile.value}
          </p>
          {tile.trend && (
            <p className="np-stat-trend" style={{ color: 'var(--bs-secondary-color)' }}>{tile.trend}</p>
          )}
        </div>
      );
      return tile.to ? (
        <Link to={tile.to} className="np-card np-card-link" key={tile.label}>{body}</Link>
      ) : (
        <div className="np-card" key={tile.label}>{body}</div>
      );
    })}
  </div>
);

export const AiDashboard: React.FC = () => {
  const navigate = useNavigate();

  const { data: useCases = [], isLoading, error } = useQuery<AiUseCase[]>({
    queryKey: aiUseCaseKeys.collection(),
    queryFn: aiUseCaseApi.list,
  });

  // owner is a real People-register foreign key (see Step2Identity.tsx).
  //
  // The real People-register records carry firstName/lastName (confirmed live against the
  // DynamoDB table), not personName - trusting personName alone renders every owner blank.
  const { data: people = [] } = useQuery<Array<{ personId?: string; personName?: string; firstName?: string; lastName?: string }>>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });
  const ownerName = (id: unknown): string => {
    const match = people.find((p) => p.personId === String(id || ''));
    if (!match) return '';
    return `${match.firstName || ''} ${match.lastName || ''}`.trim() || match.personName || '';
  };

  // Status counts — the source's four, plus `retired` so all four statuses are
  // represented (see deviations), plus `rejected` from the AI Act work.
  const stats = useMemo(() => ({
    total:     useCases.length,
    submitted: useCases.filter((u) => u.status === 'submitted').length,
    approved:  useCases.filter((u) => u.status === 'approved').length,
    active:    useCases.filter((u) => u.status === 'active').length,
    draft:     useCases.filter((u) => u.status === 'draft').length,
    rejected:  useCases.filter((u) => u.status === 'rejected').length,
    retired:   useCases.filter((u) => u.status === 'retired').length,
  }), [useCases]);

  // Privacy exposure — derived from the Step 5 data inventory already captured
  // on each use case. An addition beyond the source.
  // isPersonalData is three-valued ('no'|'yes'|'customer_controlled'), so 'customer_controlled'
  // counting as present means comparing against 'no' rather than a plain truthiness check — a
  // non-empty string is truthy regardless of which of the three values it holds. isSpecialCategory
  // stays a plain boolean, so it's a normal truthiness check.
  const privacy = useMemo(() => ({
    personal: useCases.filter((u) => (u.dataFields || []).some((f) => f.isPersonalData !== 'no')).length,
    special:  useCases.filter((u) => (u.dataFields || []).some((f) => f.isSpecialCategory)).length,
  }), [useCases]);

  // AI Act risk tiers. Counted from the stored value with no defaulting: a record written
  // before the field existed has no classification, which is `''` — unanswered, not minimal.
  const byClassification = useMemo(() => {
    const counts = {} as Record<AiActClassification, number>;
    ([...AI_ACT_CLASSIFICATIONS, ''] as AiActClassification[]).forEach((c) => { counts[c] = 0; });
    useCases.forEach((u) => {
      const tier = (u.aiActClassification || '') as AiActClassification;
      counts[tier] = (counts[tier] ?? 0) + 1;
    });
    return counts;
  }, [useCases]);

  const unassessed = useMemo(
    () => useCases.filter((u) => !isClassified((u.aiActClassification || '') as AiActClassification)).length,
    [useCases],
  );

  const byType = useMemo(
    () => AI_TYPES.map((t) => ({ type: t, count: useCases.filter((u) => u.aiType === t).length })),
    [useCases],
  );

  const recent = useMemo(
    () => [...useCases]
      .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''))
      .slice(0, RECENT_LIMIT),
    [useCases],
  );

  // Hand-written, so adding a status to AiUseCaseStatus does NOT make this array error the way
  // the Record<AiUseCaseStatus, …> maps do. `rejected` is here because someone added it here,
  // not because the compiler asked — check this array whenever the union changes.
  const statusTiles: Tile[] = [
    { label: 'Total Use Cases', value: stats.total,     icon: 'ri-list-check-2',         tone: 'primary',                to: '/ai-use-cases' },
    { label: 'Submitted',       value: stats.submitted, icon: 'ri-send-plane-line',      tone: AI_STATUS_TONE.submitted, to: '/ai-use-cases?status=submitted' },
    // Approved (decided, compliance work in progress) vs Active (fully compliant) - kept as
    // two separate tiles rather than folded together, since collapsing them back into one
    // "Active" number is exactly the "active implies compliant when it might not be" gap this
    // status split exists to close.
    { label: 'Approved',        value: stats.approved,  icon: 'ri-checkbox-circle-line', tone: AI_STATUS_TONE.approved,  to: '/ai-use-cases?status=approved' },
    { label: 'Active',          value: stats.active,    icon: 'ri-shield-check-line',    tone: AI_STATUS_TONE.active,    to: '/ai-use-cases?status=active' },
    { label: 'Drafts',          value: stats.draft,     icon: 'ri-draft-line',           tone: AI_STATUS_TONE.draft,     to: '/ai-use-cases?status=draft' },
    { label: 'Rejected',        value: stats.rejected,  icon: 'ri-close-circle-line',    tone: AI_STATUS_TONE.rejected,  to: '/ai-use-cases?status=rejected' },
    { label: 'Retired',         value: stats.retired,   icon: 'ri-archive-line',         tone: AI_STATUS_TONE.retired,   to: '/ai-use-cases?status=retired' },
  ];

  // The AI Act tier row. Driven off AI_ACT_CLASSIFICATIONS so a tier can never be added to the
  // vocabulary and silently omitted here, with the unanswered count appended — the one number
  // on this page that is not a tier and is usually the finding.
  const classificationTiles: Tile[] = [
    ...AI_ACT_CLASSIFICATIONS.map((tier) => ({
      label: AI_ACT_CLASSIFICATION_LABELS[tier],
      value: byClassification[tier] ?? 0,
      icon: AI_ACT_CLASSIFICATION_ICONS[tier],
      tone: AI_ACT_CLASSIFICATION_TONE[tier],
      // A tier's count carries its tier colour, but only when there is something in it: a
      // zero in danger red says "prohibited systems here" when the truth is the opposite.
      valueTone: (byClassification[tier] ?? 0) > 0 ? AI_ACT_CLASSIFICATION_TONE[tier] : undefined,
      to: `/ai-use-cases?classification=${encodeURIComponent(tier)}`,
    })),
    {
      label: 'Unassessed',
      value: unassessed,
      icon: AI_ACT_CLASSIFICATION_ICONS[''],
      // Green when it is zero, because "everything has been classified" is a real result and
      // deserves to read as one. Not neutral — neutral would make an empty register look done.
      tone: unassessed > 0 ? 'danger' : 'success',
      valueTone: unassessed > 0 ? 'danger' : 'success',
      trend: unassessed > 0
        ? `of ${stats.total} not yet classified`
        : 'every use case has a risk tier',
      // Trailing '=' matters: AiUseCaseList distinguishes "?classification=" (present, empty -
      // the real "not yet classified" tier) from the param being absent entirely (no filter).
      to: '/ai-use-cases?classification=',
    },
  ];

  // Privacy exposure, previously four blocks of hand-copied markup. Same data, one array.
  const privacyTiles: Tile[] = [
    {
      label: 'Involving personal data',
      value: privacy.personal,
      icon: 'ri-shield-user-line',
      tone: 'info',
      valueTone: 'info',
      trend: `of ${stats.total} registered use case${stats.total === 1 ? '' : 's'}`,
      to: '/ai-use-cases?personalData=1',
    },
    {
      label: 'Involving special-category data',
      value: privacy.special,
      icon: 'ri-shield-keyhole-line',
      tone: 'warning',
      valueTone: 'warning',
      trend: 'GDPR Article 9 — health, biometric, race and similar',
      to: '/ai-use-cases?specialCategory=1',
    },
  ];

  const mutedStyle = { color: 'var(--bs-secondary-color)' };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-robot-line"
        title="AI Governance"
        subtitle="Track, document, and assess AI use cases across your organisation."
        colour="primary"
        breadcrumbs={[{ label: 'AI Use Case Register', to: '/ai-use-cases' }, { label: 'Dashboard' }]}
        actions={
          <>
            <Link to="/ai-use-cases" className="np-btn np-btn-ghost">
              <i className="ri-list-check-2"></i>View register
            </Link>
            <Link to="/ai-use-cases/new" className="np-btn np-btn-primary">
              <i className="ri-add-line"></i>New Use Case
            </Link>
          </>
        }
      />

      {isLoading ? (
        <div className="np-card">
          <div className="np-card-body d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="np-banner np-tone-danger">
          <i className="ri-error-warning-line"></i>Failed to load AI use cases.
        </div>
      ) : (
        <>
          {/* Status counts — all-time, unfiltered. Six tiles in a five-column grid, so
              Retired wraps; the alternative was dropping a status from the row. */}
          <StatTileRow tiles={statusTiles} grid="np-grid-5" />

          {/* AI Act risk tiers — the organising concept, so it sits directly under the
              lifecycle counts and above the GDPR exposure it frames. */}
          <StatTileRow tiles={classificationTiles} grid="np-grid-5" />

          {/* Privacy exposure — derived from each use case's data inventory */}
          <StatTileRow tiles={privacyTiles} grid="np-grid-2" />

          <div className="np-grid np-grid-12-4-8">
            {/* By AI type */}
            <div className="np-card">
              <div className="np-card-head">
                <h2>By AI Type</h2>
              </div>
              <div className="np-card-body">
                {useCases.length === 0 ? (
                  <div className="text-center py-4" style={mutedStyle}>
                    <i className="ri-robot-line ri-36px d-block mb-2"></i>
                    No use cases yet
                  </div>
                ) : (
                  byType.map(({ type, count }, i) => {
                    const pct = useCases.length ? Math.round((count / useCases.length) * 100) : 0;
                    const tone = AI_TYPE_TONE[type];
                    return (
                      <div key={type} className={i === byType.length - 1 ? '' : 'mb-3'}>
                        <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
                          <div className="d-flex align-items-center gap-2">
                            <span className={`np-icon-badge-sm np-tone-${tone}`}>
                              <i className={AI_TYPE_ICONS[type]}></i>
                            </span>
                            <span style={{ fontSize: '0.828rem' }}>{AI_TYPE_LABELS[type]}</span>
                          </div>
                          <span className="fw-semibold np-tabular" style={{ fontSize: '0.828rem' }}>{count}</span>
                        </div>
                        <div className="np-progress">
                          {/* np-fill defaults to the primary colour, so only non-primary
                              tones need an explicit tone class. */}
                          <div
                            className={`np-fill${tone === 'primary' ? '' : ` np-tone-${tone}`}`}
                            style={{ width: `${pct}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Recent use cases */}
            <div className="np-card">
              <div className="np-card-head">
                <h2>Recent Use Cases</h2>
                <Link to="/ai-use-cases" className="np-btn np-btn-ghost np-btn-sm">View all</Link>
              </div>
              <div className="np-card-body p-0">
                <NimbusTable
                  thead={
                    <tr>
                      <th style={{ width: '5.5rem' }}>ID</th>
                      <th>Name</th>
                      <th style={{ width: '9.5rem' }}>Type</th>
                      <th style={{ width: '7rem' }}>Status</th>
                      <th style={{ width: '11rem' }}>Owner</th>
                    </tr>
                  }
                  isEmpty={recent.length === 0}
                  emptyState={
                    <div className="text-center py-5" style={mutedStyle}>
                      <i className="ri-robot-line ri-36px d-block mb-2"></i>
                      No AI use cases registered yet.
                    </div>
                  }
                >
                  {recent.map((uc) => (
                    <tr
                      key={uc.useCaseId}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/ai-use-cases/${uc.useCaseId}`)}
                    >
                      <td><code>{uc.useCaseId}</code></td>
                      <td className="np-row-title">
                        {uc.name || <em style={mutedStyle}>Untitled</em>}
                      </td>
                      <td>
                        {uc.aiType
                          ? <span className={`np-chip np-tone-${AI_TYPE_TONE[uc.aiType]}`}>
                              <i className={AI_TYPE_ICONS[uc.aiType]}></i>
                              {AI_TYPE_LABELS[uc.aiType]}
                            </span>
                          : <span style={mutedStyle}>—</span>}
                      </td>
                      <td>
                        <span className={`np-chip np-tone-${AI_STATUS_TONE[uc.status]}`}>
                          {AI_STATUS_LABELS[uc.status]}
                        </span>
                      </td>
                      <td style={mutedStyle}>{ownerName(uc.owner) || '—'}</td>
                    </tr>
                  ))}
                </NimbusTable>
              </div>
            </div>
          </div>

          {/* Empty-register call to action */}
          {useCases.length === 0 && (
            <div className="np-card" style={{ borderStyle: 'dashed' }}>
              <div className="np-card-body text-center py-5">
                <span className="np-icon-badge-lg np-tone-primary mx-auto mb-3">
                  <i className="ri-robot-line"></i>
                </span>
                <h6 className="mb-2">Start tracking your AI use cases</h6>
                <p className="np-sub mb-4" style={{ maxWidth: '30rem', margin: '0 auto 1.5rem' }}>
                  Register AI tools and capabilities being used across your organisation. Each use case captures
                  the business case, data handling, expected outcomes, and associated risks — all in one document.
                </p>
                <Link to="/ai-use-cases/new" className="np-btn np-btn-primary">
                  <i className="ri-add-line"></i>Register your first AI use case
                </Link>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AiDashboard;
